import { useState } from "react";
import { GlobalToken, theme, Typography, Upload, UploadProps } from "antd";
import { Question, SurveyModel } from "survey-core";
import { useMutation } from "@tanstack/react-query";
import { createStyles } from "antd-style";

const { Dragger } = Upload;

const styleUtils = createStyles(
  ({ css }, { themeToken }: { themeToken: GlobalToken }) => {
    return {
      uploadIconWrapper: css`
      width: 36px;
      display: flex;
    `,
      uploadDraggerWrapper: css`
        .ant-upload-drag {
          background-color: ${themeToken.colorInfoBg};
          border-color: ${themeToken.colorPrimary};
        }
        .ant-upload-drag-container {
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          gap: 10px !important;
          flex-direction: column !important;

          @media (max-width: 768px) {
            gap: 5px !important;
          }
        }
    `,
      uploadTextDesktop: css`
        @media (max-width: 768px) {
          display: none;
        }
    `,
      uploadTextTouch: css`
        @media (min-width: 769px) {
          display: none;
        }
    `,
    };
  },
);

type CustomRequest = NonNullable<UploadProps["customRequest"]>;
type CustomRequestOptions = Parameters<CustomRequest>[0];

export class FileUploadQuestionModel extends Question {
  getType() {
    return "fileupload";
  }
}

interface FileUploadQuestionProps {
  question: FileUploadQuestionModel;
}

type UploadVariables = {
  file: CustomRequestOptions["file"];
  onProgress?: CustomRequestOptions["onProgress"];
};

export const FileUploadQuestion = ({ question }: FileUploadQuestionProps) => {
  const { token } = theme.useToken();
  const { styles } = styleUtils({ themeToken: token });

  const survey = question.survey as SurveyModel;
  const [fileList, setFileList] = useState<UploadProps["fileList"]>(
    question.value
      ? [
          {
            uid: question.value,
            name:
              survey.getValue(`${question.name}__filename`) ?? question.value,
          },
        ]
      : [],
  );

  const uploadMutation = useMutation({
    mutationFn: async ({ file, onProgress }: UploadVariables) => {
      const formData = new FormData();
      formData.append("file", file);

      return await survey.api
        ?.post("/media", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (event) => {
            if (!onProgress || !event.total) return;
            const percent = Math.round((event.loaded / event.total) * 100);
            onProgress({ percent }, file);
          },
        })
        .then((response) => response.data);
    },
  });

  const handleChange: UploadProps["onChange"] = (info) => {
    if (info.file.status === "done") {
      const response = info.file.response;
      question.value = response.id;
      survey.setValue(`${question.name}__filename`, info.file.name);
    } else if (info.file.status === "removed") {
      question.value = undefined;
      survey.setValue(`${question.name}__filename`, undefined);
    }
    const nextList = info.fileList.slice(-1);
    setFileList(nextList);
  };

  const handleCustomRequest: CustomRequest = (options) => {
    const file = options.file;

    uploadMutation.mutate(
      { file, onProgress: options.onProgress },
      {
        onSuccess: (data) => {
          options.onSuccess?.(data, file);
        },
        onError: () => {
          options.onError?.(
            new Error(
              "Er is een fout opgetreden bij het uploaden van het bestand.",
            ),
            file,
          );
        },
      },
    );
  };

  const uploadProps: UploadProps = {
    name: "file",
    multiple: false,
    customRequest: handleCustomRequest,
    fileList,
    onChange: handleChange,
    disabled: question.isReadOnlyAttr || question.isDisabledAttr,
    classNames: {
      root: styles.uploadDraggerWrapper,
    },
    accept: {
      format: question.mimeTypes,
      // Filter will prevent the file from being uploaded if it doesn't match the accepted formats
      filter: (file) => {
        const acceptStr: string | undefined = question.mimeTypes;
        if (acceptStr === undefined) return true;
        const acceptedTypes = acceptStr.split(",").map((type) => type.trim());
        return acceptedTypes.some((type) => {
          if (type.endsWith("/*")) {
            return file.type.startsWith(type.replace("/*", "/"));
          }
          return file.type === type;
        });
      },
    },
  };

  return (
    <Dragger {...uploadProps}>
      <div className={styles.uploadIconWrapper}>
        <UploadIcon />
      </div>
      <Typography.Text className={styles.uploadTextDesktop}>
        Klik of sleep een bestand naar dit vlak om te uploaden
      </Typography.Text>
      <Typography.Text className={styles.uploadTextTouch}>
        Upload bestand
      </Typography.Text>
    </Dragger>
  );
};

// TODO replace with FA icons
const UploadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
    <path d="M344 392C344 405.3 333.3 416 320 416C306.7 416 296 405.3 296 392L296 145.9L233 208.9C223.6 218.3 208.4 218.3 199.1 208.9C189.8 199.5 189.7 184.3 199.1 175L303 71C312.4 61.6 327.6 61.6 336.9 71L441 175C450.4 184.4 450.4 199.6 441 208.9C431.6 218.2 416.4 218.3 407.1 208.9L344.1 145.9L344.1 392zM248 384L160 384C151.2 384 144 391.2 144 400L144 480C144 488.8 151.2 496 160 496L480 496C488.8 496 496 488.8 496 480L496 400C496 391.2 488.8 384 480 384L392 384L392 336L480 336C515.3 336 544 364.7 544 400L544 480C544 515.3 515.3 544 480 544L160 544C124.7 544 96 515.3 96 480L96 400C96 364.7 124.7 336 160 336L248 336L248 384zM416 440C416 426.7 426.7 416 440 416C453.3 416 464 426.7 464 440C464 453.3 453.3 464 440 464C426.7 464 416 453.3 416 440z" />
  </svg>
);
